/**
 * Subscription Context - Global subscription state management
 * Provides subscription status, trial info, and upgrade/portal actions
 */

import { createContext, useContext, useEffect, useState, useCallback, FC, ReactNode } from 'react'
import {
  SubscriptionStatus,
  getSubscriptionStatus,
  redirectToCheckout,
  redirectToPortal,
  getStatusMessage,
  canUseChat
} from '../services/subscription'

interface SubscriptionContextValue {
  // Subscription state
  subscription: SubscriptionStatus | null
  loading: boolean
  error: string | null

  // Computed properties
  isActive: boolean
  isTrialing: boolean
  isOverride: boolean
  daysRemaining: number | null
  statusMessage: string

  // Actions
  upgrade: () => Promise<void>
  manageSubscription: () => Promise<void>
  refresh: () => Promise<void>

  // Upgrade modal state (controlled by context for global access)
  showUpgradeModal: boolean
  setShowUpgradeModal: (show: boolean) => void
}

const SubscriptionContext = createContext<SubscriptionContextValue | undefined>(undefined)

interface SubscriptionProviderProps {
  children: ReactNode
}

/**
 * Subscription provider - fetches and manages subscription state
 * Should be placed high in the component tree after auth provider
 */
export const SubscriptionProvider: FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // Fetch subscription status
  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const status = await getSubscriptionStatus()
      setSubscription(status)
    } catch (err) {
      console.error('Failed to fetch subscription status:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription')
      // Set default expired state on error to prevent blocking
      setSubscription({
        status: 'expired',
        trial_ends_at: null,
        days_remaining: null,
        is_active: false,
        is_override: false,
        can_upgrade: true
      })
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch on mount
  useEffect(() => {
    refresh()
  }, [refresh])

  // Check for payment success/canceled in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const payment = params.get('payment')

    if (payment === 'success') {
      // Refresh subscription status after successful payment
      refresh()
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    } else if (payment === 'canceled') {
      // Just clean up URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [refresh])

  // Redirect to Stripe Checkout
  const upgrade = useCallback(async () => {
    try {
      await redirectToCheckout()
    } catch (err) {
      console.error('Failed to create checkout session:', err)
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
    }
  }, [])

  // Redirect to Stripe Customer Portal
  const manageSubscription = useCallback(async () => {
    try {
      await redirectToPortal()
    } catch (err) {
      console.error('Failed to open customer portal:', err)
      setError(err instanceof Error ? err.message : 'Failed to open portal')
    }
  }, [])

  // Computed properties
  const isActive = subscription ? canUseChat(subscription) : false
  const isTrialing = subscription?.status === 'trialing'
  const isOverride = subscription?.is_override ?? false
  const daysRemaining = subscription?.days_remaining ?? null
  const statusMessage = subscription ? getStatusMessage(subscription) : 'Loading...'

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        error,
        isActive,
        isTrialing,
        isOverride,
        daysRemaining,
        statusMessage,
        upgrade,
        manageSubscription,
        refresh,
        showUpgradeModal,
        setShowUpgradeModal
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}

/**
 * Hook to access subscription context
 */
export const useSubscription = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}
